import { memo, useState, useEffect, lazy, Suspense } from 'react';
import moment from 'moment';
import Tooltip from '@mui/material/Tooltip';

import './ForecastDate.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faClock } from '@fortawesome/free-solid-svg-icons';
import { DurationConstructor } from './LayerSelector.utils';

const SimpleDatePicker = lazy(() => import('../../../../components/common/SimpleDatePicker'));

enum TimeChange {
    PREVIOUS,
    NEXT,
    LAST,

}

function TimeWidget(props: { time: string, onChange: (time: string) => void, twentyFourHours?: boolean, dailyOnly?: boolean }) {
    return <select key={props.time} style={{ maxHeight: "2rem", minHeight: "1rem", minWidth: "5.5rem" }} className='form-select fs-8'
        onChange={(e) => props.onChange(e.target.value)} defaultValue={props.time}>
        {props.dailyOnly &&
            <option value="00:00">00:00</option>
            || !props.twentyFourHours &&
            <>
                <option value="00:00">00:00</option>
                <option value="12:00">12:00</option>
            </>
            ||
            [...Array(24).keys()].map(h => {
                const hourString = h.toString().padStart(2, '0');
                return <option key={hourString} value={`${hourString}:00`}>{hourString}:00</option>;
            })}
    </select>
}

export default memo(function ForecastDate(props: {
    currentForecastDate: string | null,
    onSetForecastDate: (date: string) => void,
    date?: string,
    time?: string,
    forecastIncrement?: number,
    forecastStep?: DurationConstructor,
    lastForecastDate?: string,
    twentyFourHours?: boolean,
    ignoreLatestForecastTime?: boolean,
    showTime?: boolean,
    hidePreviousAndNextLast?: boolean,
    className?: string,
    disableTime?: boolean,
    disableTimeTooltip?: string,
}) {
    const [isSafari, setIsSafari] = useState(false);
    useEffect(() => {
        setIsSafari(/^((?!chrome|android).)*safari/i.test(navigator.userAgent) && !/CriOS/i.test(navigator.userAgent));
    }, []);

    const is_valid = (dateString: string | null) => {
        return moment.utc(dateString).toString() !== "Invalid date";
    }

    // If no date is provided, use today as default
    const date = is_valid(props.currentForecastDate) ? props.currentForecastDate!.split("T")[0] : moment.utc().format("YYYY-MM-DD");
    const time = is_valid(props.currentForecastDate) ? props.currentForecastDate!.split("T")[1].replace("Z", "") : "00:00";

    const currentTime = moment.utc(`${date}T${time}Z`);
    const lastForecastMoment = moment.utc(props.lastForecastDate || "");
    const isLast = !props.ignoreLatestForecastTime && currentTime.isSame(lastForecastMoment);

    const forecastDateChanged = (newDate: string) => {
        if (!newDate) return;
        const newTime = moment.utc(`${newDate}T${time}Z`);
        props.onSetForecastDate(newTime.format("YYYY-MM-DDTHH:mm") + "Z");
    };
    const forecastTimeChanged = (value: string) => {
        if (!value) return;
        const newTime = moment.utc(`${date}T${value}Z`);
        props.onSetForecastDate(newTime.format("YYYY-MM-DDTHH:mm") + "Z");
    };
    const changeForecastTime = (direction: TimeChange) => {
        let newTime;
        switch (direction) {
            case TimeChange.PREVIOUS:
                newTime = currentTime.subtract(props.forecastIncrement || 1, props.forecastStep || 'hours');
                break;
            case TimeChange.NEXT:
                newTime = currentTime.add(props.forecastIncrement || 1, props.forecastStep || 'hours');
                if (!props.ignoreLatestForecastTime && lastForecastMoment.isBefore(newTime)) {
                    return;
                }
                break;
            case TimeChange.LAST:
                newTime = moment.utc(props.lastForecastDate || "");
                // newTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
                break;
            default:
                newTime = currentTime;
                break;
        }
        props.onSetForecastDate(newTime.format("YYYY-MM-DDTHH:mm") + "Z");
    };

    const datePickerStyle: React.CSSProperties = {
        maxWidth: "9rem",
        minWidth: "8rem",
        maxHeight: "2rem",
        minHeight: "1rem",
        border: "1px solid lightgrey",
        borderRadius: "5px",
        paddingLeft: "4px",
        paddingRight: "4px"
    };

    const nativeDatePicker = (
        <input className='fs-8' type='date' value={date} onChange={(e) => forecastDateChanged(e.target.value)}
            style={datePickerStyle}
            max={props.ignoreLatestForecastTime ? undefined : props.lastForecastDate?.split('T')[0]} />
    );

    return (<div className={props.className || 'd-flex flex-row pe-5 my-0 pb-1 mt-1'}>
        {!props.hidePreviousAndNextLast && <>
            <span id='forecast-previous' className='efas-forecast-date-button fs-8' onClick={() => changeForecastTime(TimeChange.PREVIOUS)}>
                <FontAwesomeIcon icon={faArrowLeft} />
            </span>
        </>}
        <div className='d-flex flex-row my-auto fs-8'>
            {isSafari ? (
                <Suspense fallback={nativeDatePicker}>
                    <SimpleDatePicker
                        value={date}
                        onChange={forecastDateChanged}
                        max={props.lastForecastDate?.split('T')[0]}
                        className='fs-8'
                        style={{ ...datePickerStyle, cursor: 'pointer', lineHeight: '1.8rem', whiteSpace: 'nowrap' }}
                    />
                </Suspense>
            ) : (
                nativeDatePicker
            )}
            {(props.showTime !== false) && (props.disableTime ?
                <Tooltip title={props.disableTimeTooltip || ''} placement="top" arrow disableInteractive>
                    <span>
                        <span style={{ pointerEvents: 'none' }}><TimeWidget time="00:00" onChange={() => { }} twentyFourHours={props.twentyFourHours} dailyOnly /></span>
                    </span>
                </Tooltip>
                : <TimeWidget time={time} onChange={forecastTimeChanged} twentyFourHours={props.twentyFourHours} />)}
        </div>
        {!props.hidePreviousAndNextLast && <>
            <span id='forecast-next' className={`efas-forecast-date-button fs-8 ${isLast ? 'disabled' : ''}`} onClick={() => changeForecastTime(TimeChange.NEXT)}>
                <FontAwesomeIcon icon={faArrowRight} />
            </span>
            <span id='forecast-last' className={`efas-forecast-date-button fs-7 ${isLast ? 'forecast-date-last' : 'forecast-date-not-last'}`} onClick={() => changeForecastTime(TimeChange.LAST)}>
                <FontAwesomeIcon icon={faClock} />
            </span>
        </>}
    </div>);
});